import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { useGetEntidadesQuery } from 'services/api/windx';
import {
  Entidade,
  EntidadeSimples,
  EntidadesListParams,
  Usina,
  WindxTela,
} from 'helper/windx';
import {
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  selectWindxDataSimulacao,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import SeletorConjuntos from '../SeletorConjuntos';
import SeletorSubsistemas from '../SeletorSubsistemas';
import SeletorUsinasEolicas from '../SeletorUsinasEolicas';
import './index.scss';

interface SeletorEntidadesProps {
  show: boolean;
  onHide: () => void;
  // Se `true`, "nenhuma marcada" equivale a "todas selecionadas" (backend
  // omite `id_entidades` e retorna o conjunto completo). Vale apenas para
  // a tela Comparativo SM. Em SH e Semanal o usuário precisa marcar ao
  // menos uma entidade.
  permiteSelecaoVazia?: boolean;
}

/**
 * Seletor de Entidades (FE-WINDX-004 + FE-WINDX-014 + FE-LAYOUT-003).
 *
 * Switch que delega para o componente correto baseado em `subdivisao`:
 *   - subdivisao 1/2 (subsistemas/regioes EPE): modal tradicional (grid
 *     de checkboxes inline, ~5 a 14 itens).
 *   - subdivisao 3 (conjuntos): delega para `SeletorConjuntos`, modal
 *     virtualizado com 8 colunas e agregados client-side derivados de
 *     `getUsinasEolicas` (cache compartilhado com subdivisao 4).
 *   - subdivisao 4 (usinas): delega para `SeletorUsinasEolicas`, modal
 *     virtualizado com filtros tabulares (WINDX-021).
 *
 * Para o caminho legado consome `useGetEntidadesQuery({subdivisao, tela,
 * data_simulacao})` (contrato v1.1). Backend retorna `disponivel: bool`
 * por entidade conforme EXISTS na tabela fato/agregacao. Entidades
 * indisponiveis aparecem com checkbox desabilitado e tooltip.
 */
export default function SeletorEntidades({
  show,
  onHide,
  permiteSelecaoVazia = false,
}: SeletorEntidadesProps) {
  const subdivisao = useSelector(selectWindxIdSubdivisaoEspacial);

  if (subdivisao === 1) {
    return (
      <SeletorSubsistemas
        show={show}
        onHide={onHide}
        permiteSelecaoVazia={permiteSelecaoVazia}
      />
    );
  }

  if (subdivisao === 3) {
    return (
      <SeletorConjuntos
        show={show}
        onHide={onHide}
      />
    );
  }

  if (subdivisao === 4) {
    return (
      <SeletorUsinasEolicas
        show={show}
        onHide={onHide}
        permiteSelecaoVazia={permiteSelecaoVazia}
      />
    );
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <SeletorEntidadesLegado
      show={show}
      onHide={onHide}
      permiteSelecaoVazia={permiteSelecaoVazia}
    />
  );
}

function SeletorEntidadesLegado({
  show,
  onHide,
  permiteSelecaoVazia = false,
}: SeletorEntidadesProps) {
  const dispatch = useDispatch();
  const subdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsSelecionados = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);
  const [filtro, setFiltro] = useState('');
  const [idsLocal, setIdsLocal] = useState<number[]>(idsSelecionados);

  const queryArg = useMemo<EntidadesListParams | undefined>(() => {
    if (subdivisao === undefined) return undefined;
    const arg: EntidadesListParams = { subdivisao };
    if (telaAtiva === 'sh' || telaAtiva === 'sm') {
      arg.tela = telaAtiva as WindxTela;
    }
    // WINDX-034: dataSimulacao eh fonte unica do modo de operacao.
    if (dataSimulacao) {
      arg.data_simulacao = dataSimulacao;
    }
    return arg;
  }, [subdivisao, telaAtiva, dataSimulacao]);

  const {
    data, isLoading, isFetching, error,
  } = useGetEntidadesQuery(queryArg as EntidadesListParams, {
    skip: !queryArg,
  });

  const entidadesFiltradas = useMemo<Entidade[]>(() => {
    const lista = data ?? [];
    if (!filtro.trim()) return lista;
    const q = filtro.trim().toLowerCase();
    return lista.filter((e) => {
      const ehUsina = 'codigo' in e;
      const simples = e as EntidadeSimples;
      const usina = e as Usina;
      return (
        (!ehUsina && simples.label?.toLowerCase().includes(q))
        || (!ehUsina && simples.nome?.toLowerCase().includes(q))
        || (ehUsina && usina.nome?.toLowerCase().includes(q))
        || (ehUsina && usina.codigo?.toLowerCase().includes(q))
      );
    });
  }, [data, filtro]);

  const idsIndisponiveis = useMemo<Set<number>>(() => {
    if (!data) return new Set();
    return new Set(data.filter((e) => e.disponivel === false).map((e) => e.id));
  }, [data]);

  // Auto-remove da seleção entidades que viraram indisponíveis (ex: trocou
  // tela ou data_simulacao). Atualiza tanto o buffer local quanto o Redux.
  useEffect(() => {
    if (idsIndisponiveis.size === 0) return;
    setIdsLocal((prev) => prev.filter((id) => !idsIndisponiveis.has(id)));
    const sobra = idsSelecionados.filter((id) => !idsIndisponiveis.has(id));
    if (sobra.length !== idsSelecionados.length) {
      dispatch(setIdsEntidades(sobra));
    }
  }, [idsIndisponiveis, idsSelecionados, dispatch]);

  // Sincroniza buffer local com Redux quando o modal abre.
  useEffect(() => {
    if (show) {
      setIdsLocal(idsSelecionados);
      setFiltro('');
    }
  // Intencional: idsSelecionados omitido para não sobrescrever escolhas
  // locais se outro efeito despachar ao Redux enquanto o modal está aberto.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, subdivisao]);

  const toggle = (id: number) => {
    setIdsLocal((prev) => {
      const marcando = !prev.includes(id);
      // FE-LAYOUT-011 / WINDX-025: em subdivisao=1 (Subsistemas), SIN (id=0) e
      // os 4 subsistemas individuais (SE=1, S=2, NE=3, N=4) sao mutuamente
      // exclusivos. Marcar SIN limpa os individuais e vice-versa — evita
      // dupla contagem na tabela de convergencia do Comparativo SM. Ver
      // ADR-024 (windx-definicao) para a decisao de id=0 como sentinel.
      if (subdivisao === 1 && marcando) {
        if (id === 0) return [0];
        if ([1, 2, 3, 4].includes(id)) {
          return [...prev.filter((i) => i !== 0), id];
        }
      }
      return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id];
    });
  };

  const selecionarTodosFiltrados = () => {
    const ids = entidadesFiltradas
      .filter((e) => e.disponivel !== false)
      .map((e) => e.id);
    setIdsLocal((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const limpar = () => setIdsLocal([]);

  const handleOk = () => {
    // 2026-05-29: apos aplicar a selecao, NAO dispara realizarConsulta
    // automaticamente. setIdsEntidades ja marca consultaPendente=true; o
    // usuario decide quando recarregar pelo botao "Realizar Consulta" do
    // painel — janela necessaria para que ele leia o aviso de complexidade
    // (MedidorComplexidade) antes de enviar a query. Mesmo padrao adotado
    // no ModalCadastrar de simulacoes antigas (decisao PO 2026-05-26).
    dispatch(setIdsEntidades(idsLocal));
    onHide();
  };

  const totalDisponiveis = data?.filter((e) => e.disponivel !== false).length ?? 0;
  const semSelecaoEhTodas = idsLocal.length === 0
    && permiteSelecaoVazia
    && telaAtiva === 'sm';
  const tituloModal = subdivisao === undefined
    ? 'Selecionar entidades'
    : `Selecionar entidades${idsLocal.length > 0 ? ` (${idsLocal.length} selecionada${idsLocal.length === 1 ? '' : 's'})` : ''}`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="windx-seletor-entidades-modal"
      data-testid="windx-seletor-entidades-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">{tituloModal}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {subdivisao === undefined && (
          <div className="text-muted small p-3 text-center">
            Escolha uma subdivisão espacial para listar as entidades.
          </div>
        )}

        {subdivisao !== undefined && isLoading && (
          <div className="p-3 text-center">
            <Spinner animation="border" role="status" size="sm" />
            <span className="ms-2">Carregando entidades...</span>
          </div>
        )}

        {subdivisao !== undefined && (error || (!isLoading && !data)) && (
          <Alert variant="danger" className="m-0">
            Não foi possível carregar as entidades. Tente novamente em instantes.
          </Alert>
        )}

        {subdivisao !== undefined && data && (
          <div className="windx-seletor-entidades">
            {semSelecaoEhTodas && totalDisponiveis > 0 && (
              <div
                className="windx-seletor-entidades__hint"
                data-testid="windx-seletor-entidades-hint"
              >
                <strong>Nenhuma marcada inclui todas</strong>
                {' — a consulta vai retornar dados das '}
                {totalDisponiveis}
                {' '}
                entidade
                {totalDisponiveis === 1 ? '' : 's'}
                {' disponíve'}
                {totalDisponiveis === 1 ? 'l' : 'is'}
                .
              </div>
            )}
            <div className="windx-seletor-entidades__topo">
              <Form.Group className="flex-grow-1">
                <Form.Label className="visually-hidden" htmlFor="windx-busca-entidades">
                  Buscar entidades
                </Form.Label>
                <Form.Control
                  id="windx-busca-entidades"
                  type="search"
                  placeholder="Buscar por nome..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  autoFocus
                />
              </Form.Group>
              <div className="windx-seletor-entidades__contador small text-muted">
                {semSelecaoEhTodas
                  ? `Todas as ${totalDisponiveis}`
                  : `${idsLocal.length} / ${totalDisponiveis} selecionadas`}
                {totalDisponiveis !== data.length
                  && ` — ${data.length - totalDisponiveis} sem dados`}
                {isFetching && ' — atualizando…'}
              </div>
            </div>

            <div className="windx-seletor-entidades__lista">
              {entidadesFiltradas.length === 0 ? (
                <div className="text-muted small text-center p-3">
                  Nenhuma entidade encontrada para o filtro.
                </div>
              ) : (
                entidadesFiltradas.map((e) => {
                  const simples = e as EntidadeSimples;
                  const label = simples.label || simples.nome;
                  const indisponivel = e.disponivel === false;
                  const labelComHint = indisponivel ? `${label} (sem dados)` : label;
                  const inputId = `windx-entidade-${e.id}`;
                  return (
                    <label
                      key={e.id}
                      htmlFor={inputId}
                      className={`windx-seletor-entidades__item ${indisponivel ? 'is-indisponivel' : ''}`}
                      title={indisponivel ? 'Sem dados disponíveis para a tela / data selecionada' : undefined}
                    >
                      <input
                        type="checkbox"
                        id={inputId}
                        className="form-check-input"
                        checked={idsLocal.includes(e.id)}
                        onChange={() => toggle(e.id)}
                        disabled={indisponivel}
                      />
                      <span className="windx-seletor-entidades__item-texto">
                        {labelComHint}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={limpar}
          disabled={idsLocal.length === 0}
        >
          Limpar
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={selecionarTodosFiltrados}
          disabled={
            entidadesFiltradas.length === 0
            || entidadesFiltradas.every((e) => e.disponivel === false)
          }
        >
          Selecionar todos
        </Button>
        <Button size="sm" onClick={handleOk}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
